<template>
  <GamMainView class="players-view">
    <ResultListView>
      <GamListView v-bind="getPlayerList" is-horizontally-padded :key="`refreshed-at-${lastRefreshedAt}`">
        <template #header>
          <div :class="['top-nav', { 'is-mobile': isMobile }]">
            <GamButton
              v-if="!isMobile"
              class="back-button"
              :left-icon="GamIconName.ARROW_LEFT"
              is-icon-only
              :size="GamButtonSize.SMALL"
              :variant="GamButtonVariant.STEKLO"
              @click="handleBack"
            />

            <PlayersListHeader :class="['header', { 'is-mobile': isMobile }]" />
          </div>

          <Vue3PullToRefresh
            :distance="62"
            :duration="300"
            :size="32"
            noreload
            :options="{ color: '#CCC', bgColor: '#282828' }"
            @onrefresh="handleRefresh"
          />
        </template>

        <template #list>
          <template v-if="filteredItemList?.length">
            <div class="list-section-label">{{ localize(sectionLabel) }}</div>

            <GamPlayerResultRow
              v-for="player in filteredItemList"
              v-bind="getPlayerCard(player)"
              :id="player.id"
              :key="`player-${player.id}`"
              @click="navigateToPlayer(player.username)"
            />
          </template>
          <template v-else-if="isLoading">
            <div class="skeleton-loader">
              <div class="skeleton-item skeleton o-2" />
              <div class="skeleton-item skeleton o-3" />
              <div class="skeleton-item skeleton o-4" />
            </div>
          </template>
        </template>
      </GamListView>
    </ResultListView>

    <div v-if="isMobile" class="nav-close-btn" @click="handleBack">
      <GamIcon :name="GamIconName.X_CLOSE" :size="GamIconSize.X_LARGE" is-current />
    </div>

    <GamDetailView v-if="!isMobile" :list-id="listId" :is-open="!!route.params.username">
      <ScrollOverflow v-if="lastUsername" class="detail-scroll" is-vertical>
        <PlayerDetail :username="lastUsername" :key="`player-id-${lastUsername}-${lastRefreshedDetailsAt}`">
          <template #pull-to-refresh>
            <Vue3PullToRefresh
              :distance="50"
              :duration="300"
              :size="32"
              noreload
              :options="{ color: '#CCC', bgColor: '#282828' }"
              @onrefresh="handleRefreshDetails"
            />
          </template>
        </PlayerDetail>
      </ScrollOverflow>
      <PlaceholderSplash v-else-if="!isLoading && false" :heading="randomPun" />
    </GamDetailView>
  </GamMainView>
</template>

<script lang="ts" setup>
import { RouterNameType } from '@/core/data/config/uiConfig.interface';
import type { GeoCoords } from '@/core/data/location/location.interface';
import type { BaseUserDto } from '@/core/data/user/user.interface';
import { gambits, localize } from '@/core/gambits';
import ListHelper, { isValidLocation, listInfo } from '@/core/helpers/list.helper';
import LocationHelper from '@/core/helpers/location.helper';
import { isMobile } from '@/core/helpers/ui.helper';
import type { GetPlayerTableFilters } from '@/core/network/http/httpClient.interface';
import { useAuthStore } from '@/stores/auth.store';
import { useFilterStore } from '@/stores/filter.store';
import { useGeolocationStore } from '@/stores/geolocation.store';
import { useTableStore } from '@/stores/table.store';
import GamButton from '@/views/components/GamButton.vue';
import GamIcon from '@/views/components/GamIcon.vue';
import GamMainView from '@/views/components/GamMainView.vue';
import PlaceholderSplash from '@/views/components/PlaceholderSplash.vue';
import ResultListView from '@/views/components/ResultListView.vue';
import ScrollOverflow from '@/views/components/ScrollOverflow.vue';
import GamDetailView from '@/views/components/detail-view/GamDetailView.vue';
import GamListView from '@/views/components/gam-list-view/GamListView.vue';
import GamPlayerResultRow from '@/views/components/gam-player/GamPlayerResultRow.vue';
import { GamButtonSize, GamButtonVariant } from '@/views/composables/constants/components/gamButton.constants';
import { GamIconName, GamIconSize } from '@/views/composables/constants/components/gamIcon.constants';
import { GamListId } from '@/views/composables/constants/components/gamIntersect.constants';
import { GamPlayerCardSize } from '@/views/composables/constants/components/gamPlayer.constants';
import { GamFilterSortPlayer } from '@/views/composables/constants/main/filter.constants';
import type { GamListType } from '@/views/composables/models/components/GamList';
import type { GamPlayerCardType } from '@/views/composables/models/components/GamPlayer';
import PlayersListHeader from '@/views/main/players/PlayersListHeader.vue';
// @ts-ignore
import Vue3PullToRefresh from '@amirafa/vue3-pull-to-refresh';
import { storeToRefs } from 'pinia';
import { nextTick } from 'process';
import { computed, onBeforeMount, onMounted, onUnmounted, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import PlayerDetail from './PlayerDetail.vue';

const PUNS = [
  'Playing over-the-board can lead to chilling with friends.',
  'Turn an afternoon drink with friends into a knightmare.',
];

const randomPun = computed(() => (window.isVisualTest ? PUNS[0] : PUNS[Math.floor(Math.random() * PUNS.length)]));

const listId = GamListId.PLAYERS;
const tableStore = useTableStore<BaseUserDto>(listId)();
const { itemList, tableParams, isLoading } = storeToRefs(tableStore);
const filterStore = useFilterStore<GetPlayerTableFilters>(listId)();

const route = useRoute();
const router = useRouter();

const authStore = useAuthStore();
const { userDetails } = storeToRefs(authStore);

const geolocationStore = useGeolocationStore();
const { userLocation } = storeToRefs(geolocationStore);

const isSearching = computed(() => !!tableParams.value.search?.length);
const hasOwnLocation = computed(() => isValidLocation(userLocation.value?.location));

const filteredItemList = computed(() => {
  if (!itemList.value || !userDetails.value?.id) return itemList.value;
  return itemList.value.filter((player) => player.id !== userDetails.value?.id);
});

const lastRefreshedAt = ref<number>(new Date().getTime());
const lastRefreshedDetailsAt = ref<number>(new Date().getTime());

const lastUsername = ref<string | null>(null);
watch(
  () => route.params.username,
  (newUsername, oldUsername) => {
    if (!newUsername || newUsername === oldUsername) return;

    lastUsername.value = String(newUsername);
  },
);
onMounted(() => {
  if (!route.params.username) return;

  lastUsername.value = String(route.params.username);
});

/**
 * Anchor for the nearby list. Pinned so that paging through results keeps
 * measuring from one point, and only re-taken once the player actually moves.
 */
const anchor = ref<GeoCoords | null>(null);

const pinAnchor = () => {
  const location = userLocation.value?.location;
  anchor.value = location && isValidLocation(location) ? { lat: location.lat, lng: location.lng } : null;
};

onBeforeMount(() => {
  filterStore.setFilters({
    sortBy: GamFilterSortPlayer.DISTANCE,
  });

  pinAnchor();
});

watch(userLocation, () => {
  const location = userLocation.value?.location;
  if (!isValidLocation(location)) return;
  if (!LocationHelper.shouldUpdateUserLocation(anchor.value ?? undefined, location)) return;

  pinAnchor();

  if (isSearching.value) return;
  tableStore.loadItems({ selectFirstItem: !isMobile.value });
});

const requestLocation = async () => {
  await geolocationStore.requestFreshPosition();
};

const getPlayerCard = (player: BaseUserDto): GamPlayerCardType => {
  return {
    player,
    selected: player.username === String(route.params.username),
    size: GamPlayerCardSize.NORMAL,
  };
};

const handleRefresh = () => {
  lastRefreshedAt.value = new Date().getTime();
};

const handleRefreshDetails = () => {
  lastRefreshedDetailsAt.value = new Date().getTime();
};

const navigateToPlayer = (username: string) => {
  const playerRoute = !isMobile.value
    ? { name: RouterNameType.PLAYERS, params: { username }, query: route.query }
    : { name: RouterNameType.USERNAME, params: { username }, query: { back: 'history' } };

  router.push(playerRoute);
};

const handleBack = () => {
  router.push({ name: RouterNameType.USERNAME, params: { username: userDetails.value?.username } });
};

const sectionLabel = computed(() => (isSearching.value ? 'players.list.section.all' : 'players.list.section.nearby'));

const endInfo = computed(() => {
  // Search results end without a sign-off; there is nothing useful to say.
  if (isSearching.value) return undefined;

  return listInfo({ id: listId, title: 'players.list.nearby.end' });
});

const noDataInfo = computed(() => {
  if (isSearching.value) {
    return ListHelper.getListNoData({ id: listId, message: 'players.list.search.notFound' });
  }

  // An empty nearby list without a location means we simply do not know where
  // the player is, so ask for it instead of claiming nobody is around.
  if (!hasOwnLocation.value) {
    return listInfo({
      id: listId,
      title: 'players.list.location.title',
      message: 'players.list.location.message',
      label: 'location.permission.modal.cta',
      onClick: requestLocation,
    });
  }

  return listInfo({
    id: listId,
    title: 'players.list.nearby.empty',
    message: 'players.list.nearby.empty.message',
  });
});

const getPlayerList = computed((): GamListType<BaseUserDto> => {
  return {
    listId,
    endInfo: endInfo.value,
    noDataInfo: noDataInfo.value,
    scrollSpacing: true,
    isHorizontallyPadded: true,
    loadCallback: (listId) => {
      return gambits.userService.getUsers(listId, { nearby: true, location: anchor.value });
    },
  };
});

watch(
  () => ({ username: route.params.username, items: itemList.value, isMobile: isMobile.value }),
  async ({ username, items, isMobile }) => {
    if (!username && !isMobile && items?.length) {
      username = items[0].username;
      nextTick(() => {
        router.replace({ name: RouterNameType.PLAYERS, params: { username }, query: route.query });
      });
    }

    if (!username && items?.length) {
      username = items[0].username;
    }
  },
);

onUnmounted(() => {
  tableStore.resetList(true);
});
</script>

<style scoped lang="scss">
.detail-scroll {
  width: 100%;
  height: 100svh;
  display: flex;
  flex-direction: column;
}

.player-tab-selector {
  display: flex;
  justify-self: center;
  margin: 0 auto;
  padding: var(--spacing-xxl) 0;
  flex-grow: 1;
  justify-content: flex-end;

  .tab-container {
    display: flex;
    flex-direction: row;
    gap: 24px;
  }
}
</style>

<style lang="scss">
.main-view.players-view .list-content {
  gap: var(--spacing-md) !important;
}

.players-view .missing-info-container {
  padding-bottom: calc(3 * var(--spacing-xxl) + var(--spacing-xl));
}
</style>

<style lang="scss" scoped>
@use '@/ui/css/mixins' as *;
.top-nav {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  position: relative;
}

.header {
  width: 100%;
}

.top-nav:not(.is-mobile) :deep(.list-header) {
  align-items: flex-end;
}

.header :deep(.gam-tab) {
  width: calc(100% - 3rem);
}

.back-button {
  position: absolute !important;
  top: 0;
  left: 0;
}

.list-section-label {
  width: 100%;
  text-align: center;
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 0.15em;
  text-transform: uppercase;
  color: var(--color-white-30);
  padding-bottom: var(--spacing-xs);
}

.nav-close-btn {
  position: fixed;
  bottom: var(--safe-bottom-offset);
  left: 50%;
  transform: translateX(-50%);
  width: 3.5rem;
  height: 3.5rem;
  border-radius: 3.5rem;
  border: 1px solid var(--color-white-10);
  backdrop-filter: blur(8px);
  background-color: var(--color-dark-5);
  color: var(--color-white-70);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  z-index: 1000;
  transition: all 200ms linear;

  @include hover {
    background-color: var(--color-dark-20);
    border-color: var(--color-white-30);
    color: var(--color-white);
  }

  @include press($transform: translateX(-50%));
}
</style>
